import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { Skeleton } from 'primereact/skeleton';
import { url } from '../../../../env';
import { AppIconButton } from '../../../../Components/FormKit';
import { fetchPdfData, mensajeDeError } from '../../../../services/adminRecotizacionApi';
import { buildBudgetPdfFile } from '../../../Cotizador/PDFComponent/buildBudgetPdfFile';
import { budgetsManagementCSS, sourceChipCSS } from './BudgetsManagement.css';
import BudgetDetailModal from './BudgetDetailModal';
import RecotizarBudgetDialog from './RecotizarBudgetDialog';

/* ─── KPI definitions ─── */
const KPI_DEFS = [
    { key: 'total', label: 'Todas',  source: null, tone: 'all',  icon: 'pi pi-file' },
    { key: 'pei',   label: 'Pei',    source: 'pei',  tone: 'pei',  icon: 'pi pi-check-circle' },
    { key: 'trim',  label: 'Trim',   source: 'trim', tone: 'trim', icon: 'pi pi-star' },
];

const PER_PAGE = 15;

const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

/* ─── Helpers ─── */
const formatCurrency = (v) =>
    v != null ? `$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const now = new Date();
        
        // Reset hours/minutes/seconds for date-only comparison
        const dDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        const diffTime = nowDate.getTime() - dDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) {
            // Future date fallback
            return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
        }
        
        if (diffDays === 0) {
            return 'hoy';
        }
        if (diffDays === 1) {
            return 'ayer';
        }
        if (diffDays < 7) {
            return `hace ${diffDays} días`;
        }
        
        const day = String(d.getDate()).padStart(2, '0');
        const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
        const month = months[d.getMonth()];
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    } catch {
        return '—';
    }
};

/** Determina si la persona solo jugó con el cotizador sin dejar datos de contacto */
const isSoloCotizo = (b) => !b.email && !b.phone && !b.user_id;

/** Label legible de la fuente */
const sourceLabel = (source) => {
    if (source === 'pei') return 'Pei';
    if (source === 'trim') return 'Trim';
    if (source === 'cliente') return 'Cliente';
    return source || '—';
};

/* ─── Skeleton placeholders ─── */
const KpiCardsSkeleton = () => (
    <div className="bm-kpis">
        {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bm-kpi bm-kpi-skeleton" aria-busy="true">
                <Skeleton shape="circle" size="36px" borderRadius="10px" />
                <span className="bm-kpi-body">
                    <Skeleton width="55%" height="0.62rem" borderRadius="4px" />
                    <Skeleton width="40%" height="1.5rem" borderRadius="4px" />
                </span>
            </div>
        ))}
    </div>
);

const TableRowsSkeleton = ({ rows = 8 }) => (
    <>
        {Array.from({ length: rows }).map((_, i) => (
            <tr key={`skel-${i}`} className="bm-row-skeleton" aria-busy="true">
                <td><Skeleton width="24px" height="0.78rem" borderRadius="4px" /></td>
                <td><Skeleton width="70%" height="0.85rem" borderRadius="4px" /></td>
                <td><Skeleton width="85%" height="0.78rem" borderRadius="4px" /></td>
                <td><Skeleton width="75%" height="0.78rem" borderRadius="4px" /></td>
                <td><Skeleton width="65%" height="0.78rem" borderRadius="4px" /></td>
                <td><Skeleton width="72px" height="0.78rem" borderRadius="4px" /></td>
                <td><Skeleton width="55px" height="0.78rem" borderRadius="4px" /></td>
                <td><Skeleton width="52px" height="18px" borderRadius="999px" /></td>
                <td><Skeleton width="72px" height="0.78rem" borderRadius="4px" /></td>
            </tr>
        ))}
    </>
);

/* ═══════════════════════════════════════════════════════════════════ */
const BudgetsManagement = ({ toast }) => {
    const [sourceFilter, setSourceFilter] = useState(null);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);

    const [budgets, setBudgets] = useState([]);
    const [meta, setMeta] = useState({ total: 0, per_page: PER_PAGE, current_page: 1, last_page: 1, kpis: {} });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedBudget, setSelectedBudget] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [descargando, setDescargando] = useState(false);
    const [recotizarVisible, setRecotizarVisible] = useState(false);

    // Debounce
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search.trim()), 400);
        return () => clearTimeout(id);
    }, [search]);

    // Reset page on filter change
    useEffect(() => { setPage(1); }, [sourceFilter, debouncedSearch]);

    const fetchBudgets = useCallback(async (signal) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (sourceFilter) params.append('source', sourceFilter);
            if (debouncedSearch) params.append('search', debouncedSearch);
            params.append('page', String(page));

            const res = await axios.get(`${url}/getAllBudgets?${params.toString()}`, {
                ...authHeader(),
                signal,
            });
            const rows = res.data?.data ?? [];
            setBudgets(rows);
            setMeta(res.data?.meta ?? { total: 0, per_page: PER_PAGE, current_page: 1, last_page: 1, kpis: {} });
            return rows;
        } catch (e) {
            if (axios.isCancel(e)) return;
            console.error(e);
            setError('No se pudieron cargar las cotizaciones.');
            toast?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudieron cargar las cotizaciones.',
                life: 4000,
            });
        } finally {
            setLoading(false);
        }
    }, [sourceFilter, debouncedSearch, page, toast]);

    useEffect(() => {
        const controller = new AbortController();
        fetchBudgets(controller.signal);
        return () => controller.abort();
    }, [fetchBudgets]);

    const openBudget = (b) => {
        setSelectedBudget(b);
        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setSelectedBudget(null);
        setRecotizarVisible(false);
    };

    // Mismo PDF que baja el cliente por su enlace (D-ADM-6), sin pasar por el secret.
    const descargarPdf = async () => {
        if (!selectedBudget || descargando) return;
        const id = selectedBudget.id;
        setDescargando(true);
        try {
            const data = await fetchPdfData(id);
            const file = await buildBudgetPdfFile(data, `cotizacion_${id}.pdf`);
            saveAs(file, file.name);
        } catch (e) {
            console.error(e);
            toast?.current?.show({
                severity: e?.response?.status === 422 ? 'warn' : 'error',
                summary: 'No se pudo descargar el PDF',
                detail: mensajeDeError(e, 'No se pudo generar el PDF.'),
                life: 6000,
            });
        } finally {
            setDescargando(false);
        }
    };

    // Tras recotizar: recarga la lista y deja el detalle con la fila nueva
    // (la lista va por created_at, la fila no cambia de página). Si ya no
    // está en esta página o en la búsqueda, se cierra el detalle.
    const alRecotizar = async () => {
        const id = selectedBudget?.id;
        const rows = await fetchBudgets();
        const fila = rows?.find((b) => b.id === id);
        if (fila) setSelectedBudget(fila);
        else closeModal();
    };

    const goPrev = () => page > 1 && setPage(page - 1);
    const goNext = () => page < meta.last_page && setPage(page + 1);

    const startItem = meta.total > 0 ? (meta.current_page - 1) * meta.per_page + 1 : 0;
    const endItem = Math.min(meta.current_page * meta.per_page, meta.total);
    const kpis = meta.kpis ?? {};

    return (
        <div css={budgetsManagementCSS()} className="fk-root">
            <header className="bm-header">
                <div>
                    <h1 className="bm-title">Cotizaciones</h1>
                    <p className="bm-subtitle">Todas las cotizaciones generadas en la plataforma.</p>
                </div>
            </header>

            {/* ── KPI cards ── */}
            {loading && budgets.length === 0 ? (
                <KpiCardsSkeleton />
            ) : (
                <div className="bm-kpis">
                    {KPI_DEFS.map((def) => {
                        const isActive = (def.source ?? null) === (sourceFilter ?? null);
                        const value = kpis[def.key] ?? 0;
                        return (
                            <button
                                type="button"
                                key={def.key}
                                className={`bm-kpi tone-${def.tone} ${isActive ? 'active' : ''}`}
                                onClick={() => setSourceFilter(def.source ?? null)}
                                aria-pressed={isActive}
                            >
                                <span className="bm-kpi-icon"><i className={def.icon} /></span>
                                <span className="bm-kpi-body">
                                    <span className="bm-kpi-label">{def.label}</span>
                                    <span className="bm-kpi-value">{value}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* ── Search toolbar ── */}
            <div className="bm-toolbar">
                <div className="bm-search">
                    <span className="bm-search-icon">
                        <i className={loading ? 'pi pi-spin pi-spinner' : 'pi pi-search'} />
                    </span>
                    <input
                        type="text"
                        placeholder="Buscar por nombre, email o teléfono"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* ── Table ── */}
            <div className="bm-table-card">
                <div className="bm-table-scroll">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Cliente</th>
                                <th>Email</th>
                                <th>Teléfono</th>
                                <th>Auto</th>
                                <th>Renta Mensual</th>
                                <th>Plazo</th>
                                <th>Fuente</th>
                                <th>Fecha</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <TableRowsSkeleton rows={8} />}

                            {!loading && error && (
                                <tr>
                                    <td colSpan={9}>
                                        <div className="bm-empty">
                                            <i className="pi pi-exclamation-triangle" style={{ color: '#ef4444' }} />
                                            <span>{error}</span>
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {!loading && !error && budgets.length === 0 && (
                                <tr>
                                    <td colSpan={9}>
                                        <div className="bm-empty">
                                            <i className="pi pi-file" />
                                            <span>No se encontraron cotizaciones</span>
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {!loading && !error && budgets.map((b) => {
                                const v = b.vehicle;
                                const autoLabel = v
                                    ? `${v.brand || ''} ${v.model || ''} ${v.year || ''}`.trim()
                                    : '—';

                                const soloCotizo = isSoloCotizo(b);

                                return (
                                    <tr key={b.id} onClick={() => openBudget(b)}>
                                        <td>{b.id}</td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                                {b.name || '—'}
                                                {soloCotizo && (
                                                    <span style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '0.25rem',
                                                        fontSize: '0.62rem',
                                                        fontWeight: 700,
                                                        padding: '2px 7px',
                                                        borderRadius: '999px',
                                                        background: '#fef3c7',
                                                        color: '#92400e',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        <i className="pi pi-eye" style={{ fontSize: '0.58rem' }} />
                                                        Solo cotizó
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ color: soloCotizo ? '#94a3b8' : undefined }}>
                                            {b.email || (soloCotizo ? 'Sin datos' : '—')}
                                        </td>
                                        <td style={{ color: soloCotizo ? '#94a3b8' : undefined }}>
                                            {b.phone || (soloCotizo ? 'Sin datos' : '—')}
                                        </td>
                                        <td style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {autoLabel}
                                        </td>
                                        <td className="bm-td-currency">{formatCurrency(b.renta_mensual_total ?? b.monthly_rent)}</td>
                                        <td>{b.term ? `${b.term} meses` : '—'}</td>
                                        <td>
                                            {b.source ? (
                                                <span css={sourceChipCSS(b.source)}>
                                                    {sourceLabel(b.source)}
                                                </span>
                                            ) : '—'}
                                        </td>
                                        <td>{formatDate(b.created_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* ── Pagination ── */}
                {!loading && !error && budgets.length > 0 && (
                    <div className="bm-pagination">
                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>
                            Mostrando <strong style={{ color: '#0f172a' }}>{startItem}</strong> a{' '}
                            <strong style={{ color: '#0f172a' }}>{endItem}</strong> de{' '}
                            <strong style={{ color: '#0f172a' }}>{meta.total}</strong> resultados
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <AppIconButton
                                icon="pi pi-chevron-left"
                                onClick={goPrev}
                                disabled={page <= 1}
                                title="Anterior"
                            />
                            <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>
                                {meta.current_page} / {meta.last_page}
                            </span>
                            <AppIconButton
                                icon="pi pi-chevron-right"
                                onClick={goNext}
                                disabled={page >= meta.last_page}
                                title="Siguiente"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* ── Detail Modal ── */}
            <BudgetDetailModal
                budget={selectedBudget}
                visible={modalVisible}
                onHide={closeModal}
                onDescargar={descargarPdf}
                onRecotizar={() => setRecotizarVisible(true)}
                descargando={descargando}
            />

            {/* ── Recotizar (encima del detalle) ── */}
            <RecotizarBudgetDialog
                budgetId={selectedBudget?.id}
                visible={recotizarVisible && Boolean(selectedBudget)}
                onHide={() => setRecotizarVisible(false)}
                onDone={alRecotizar}
                toast={toast}
            />
        </div>
    );
};

export default BudgetsManagement;
